import { Box, Typography } from "@mui/material";
import {
  incidentTypes,
  type IncidentCardContainerProps,
  type IncidentCardProperties,
  type IncidentInformationCardProps,
  type IncidentType,
  type PotentialBufferValues,
} from "../../types";
import { styleMapper, Title } from "../styles";
import { createCardData } from "./createCardData";
import { globalStyles } from "../../../../../../../styles/globalStyleTheming";

const isIncidentType = (
  value: IncidentInformationCardProps["type"],
): value is IncidentType =>
  Boolean(value && incidentTypes.includes(value as IncidentType));

const isIncidentCardProperties = (
  properties: IncidentInformationCardProps["properties"],
): properties is IncidentCardProperties =>
  Boolean(
    properties &&
    typeof properties === "object" &&
    typeof properties.incident === "string",
  );

const IncidentCardContainer = ({
  children,
  borderColour,
  backgroundColor,
  pointerEvents = "none",
}: IncidentCardContainerProps) => {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 12,
        right: 12,
        width: 320,
        maxWidth: "calc(100% - 24px)",
        maxHeight: "calc(100% - 24px)",
        overflowY: "auto",
        backgroundColor,
        border: `2px solid ${borderColour}`,
        borderRadius: 1,
        padding: 1.5,
        zIndex: 1,
        boxShadow: 2,
        pointerEvents,
        scrollbarColor: `${borderColour} ${backgroundColor}`,
        "&::-webkit-scrollbar": {
          width: 10,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: borderColour,
          borderRadius: 999,
          border: `2px solid ${backgroundColor}`,
        },
      }}
    >
      {children}
    </Box>
  );
};

function IncidentInformationCard({
  properties,
  type,
  apiRef,
}: IncidentInformationCardProps) {
  if (!properties || !type) return null;

  if (type === "outline") {
    return (
      <IncidentCardContainer
        borderColour={styleMapper.outline.colour}
        backgroundColor={globalStyles.palette.brand.plain}
      >
        <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
          Project boundaries
        </Typography>
        <Typography sx={{ fontSize: "12px", paddingTop: 0.5 }}>
          Project boundary as it has been uploaded for this project.
        </Typography>
      </IncidentCardContainer>
    );
  }

  if (type === "buffer") {
    return (
      <IncidentCardContainer
        borderColour={styleMapper.buffer.colour}
        backgroundColor={globalStyles.palette.brand.plain}
      >
        <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
          Buffer
        </Typography>
        <Typography sx={{ fontSize: "12px", paddingTop: 0.5 }}>
          Buffer area as it had been set:{" "}
          {properties.buffer as PotentialBufferValues}km
        </Typography>
      </IncidentCardContainer>
    );
  }

  if (!isIncidentType(type) || !isIncidentCardProperties(properties))
    return null;

  const cardData = createCardData(properties, apiRef);

  return (
    <IncidentCardContainer
      borderColour={styleMapper[type].colour}
      backgroundColor={styleMapper[type].background}
      pointerEvents="auto"
    >
      <Title style={{ color: cardData.titleColour }}>{cardData.title}</Title>

      {cardData.paragraphs.map((paragraph, index) => (
        <p key={`${paragraph}-${index}`}>{paragraph}</p>
      ))}

      {cardData.links.map((link) => (
        <a
          key={link.href}
          href={link.href}
          style={{ margin: 2 }}
          target="_blank"
          rel="noreferrer"
        >
          {link.label}
        </a>
      ))}
    </IncidentCardContainer>
  );
}

export default IncidentInformationCard;
